import { BoardMenu } from '@atlassian/jira-automation-menu/src/ui/main.tsx';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Capability } from '../../common/capability/index.tsx';
import { CAN_EDIT_BOARD, CAN_MANAGE_AUTOMATION } from '../../model/permission/permission-types.tsx';
import { getPermissionsSelector } from '../../state/selectors/board/board-permissions-selectors.tsx';
import {
	getIsHeaderCompactMode,
	isStandupsEnabled,
} from '../../state/selectors/board/board-selectors.tsx';
import { getCapability } from '../../state/selectors/capability/capability-selectors.tsx';
import {
	selectedIssueKeysSelector,
	selectedIssueIdsSelector,
} from '../../state/selectors/card/card-selectors.tsx';
import {
	contextPathSelector,
	getLocale,
	getIsCMPBoard,
	getIsIncrementPlanningBoard,
	getBoardName,
	getBoardType,
	projectIdSelector,
	projectKeySelector,
	projectNameSelector,
	rapidViewIdSelector,
	getBoardTypeForAnalytics,
	getIsJSWBoard,
} from '../../state/selectors/software/software-selectors.tsx';
import {
	remainingDaysSelector,
	activeSprintGoalSelector,
	hasActiveSprintsSelector,
	isSprintsEnabled,
	hasManageSprintPermission,
} from '../../state/selectors/sprint/sprint-selectors.tsx';
import { isReleasesFeatureEnabled } from '../../state/selectors/work/work-selectors.tsx';
import type { State } from '../../state/types.tsx';
import CompleteSprintButton from './complete-sprint-button/index.tsx';
import ControlsBar from './controls-bar/index.tsx';
import BoardOperations from './extension/index.tsx';
import FavoriteButton from './favourite-button/index.tsx';
import Menu from './menu/index.tsx';
import ReleaseVersion from './release-version/index.tsx';
import SprintRemainingDays from './sprint-remaining-days/index.tsx';
import Title from './title/index.tsx';
import { getIsCompleteSprintButtonVisible } from './utils.tsx';
import Header, { type StateProps } from './view.tsx';

export const mapStateToProps = (state: State): StateProps => {
	const isTabNavigation = getCapability(state)(Capability.IS_TAB_NAVIGATION);

	const isCompleteSprintButtonVisible = getIsCompleteSprintButtonVisible(state);

	// We should remove Sprint Remaining Days entirely when removing Capability.IS_TAB_NAVIGATION
	const isSprintRemainingDaysVisible =
		!isTabNavigation && !!remainingDaysSelector(state).remainingDays;
	const isCMPBoard = getIsCMPBoard(state);
	const isIncrementPlanningBoard = getIsIncrementPlanningBoard(state);
	const boardType = getBoardType(state);
	const isJSWBoard = getIsJSWBoard(state);

	return {
		baseUrl: contextPathSelector(state),
		locale: getLocale(state),
		sprintGoal: activeSprintGoalSelector(state),
		isMenuVisible:
			!!getPermissionsSelector(state)[CAN_EDIT_BOARD] || isCMPBoard || getWillShowNav4(),
		isHeaderCompactMode: getIsHeaderCompactMode(state),
		isCMPBoard,
		isJSWBoard,
		isIncrementPlanningBoard,
		rapidViewId: rapidViewIdSelector(state),
		Menu,
		ReleaseVersion,
		isReleaseVersionVisible:
			isCMPBoard && boardType === 'KANBAN' && isReleasesFeatureEnabled(state),
		ControlsBar,
		Title,
		// New AutomationMenu is NOT just for CMP now. We should use it for TMP as well
		AutomationMenu: BoardMenu,
		isCompleteSprintButtonVisible,
		hasManageSprintPermission: hasManageSprintPermission(state),
		CompleteSprintButton,
		FavoriteButton,
		isSprintRemainingDaysVisible,
		SprintRemainingDays,
		BoardOperations,
		BreadcrumbRenderer,
		// For UIF this will always be true but the AutomationMenu is permission aware
		canManageAutomations: !!getPermissionsSelector(state)[CAN_MANAGE_AUTOMATION],
		projectKey: projectKeySelector(state),
		projectId: projectIdSelector(state),
		projectName: projectNameSelector(state),
		projectType: 'software',
		isSprintsEnabled: isSprintsEnabled(state),
		shouldShowStandupsFeature: fg('jira-warepil-standup-toggle') ? isStandupsEnabled(state) : true,
		hasActiveSprint: hasActiveSprintsSelector(state),
		selectedIssueKeys: selectedIssueKeysSelector(state),
		selectedIssueIds: selectedIssueIdsSelector(state),
		boardName: getBoardName(state),
		shouldRenderFavouriteButton: getCapability(state)(
			Capability.RENDER_BOARD_FAVOURITE_HEADER_BUTTON,
		),
		shouldRenderAutomationButton: getCapability(state)(
			Capability.RENDER_BOARD_AUTOMATION_HEADER_BUTTON,
		),
		shouldRenderReleaseButton: getCapability(state)(Capability.RENDER_BOARD_RELEASE_MENU_BUTON),
		shouldRenderFeedbackCollectorButton: getCapability(state)(
			Capability.RENDER_BOARD_FEEDBACK_COLLECTOR_BUTTON,
		),
		shouldRenderFullscreenButton: getCapability(state)(Capability.RENDER_BOARD_FULLSCREEN_BUTTON),
		shouldRenderViewSettingsCardDetailsOption: getCapability(state)(
			Capability.RENDER_VIEW_SETTINGS_CARD_DETAILS_OPTION,
		),
		boardType: getBoardTypeForAnalytics(state),
		isTabNavigation,
	};
};

export default connect(mapStateToProps, {})(Header);
